import React, { useState } from 'react';
import './Help.css';

const FAQ = ({ onClose }) => {
  const [openItem, setOpenItem] = useState(null);

  const toggleItem = (index) => {
    setOpenItem(openItem === index ? null : index);
  };

  const faqItems = [
    {
      category: "Getting Started",
      items: [
        {
          question: "What wallets are supported?",
          answer: "We support all major Hedera wallets including HashPack, Blade, Kabila, and WalletConnect-compatible wallets. Make sure your wallet is connected to the Hedera mainnet."
        },
        {
          question: "How do I connect my wallet?",
          answer: "Click the wallet button in the top-right corner and select your preferred wallet. Follow the prompts to connect. Make sure you have your wallet extension installed and unlocked."
        },
        {
          question: "Why can't I see my NFTs?",
          answer: "Make sure your wallet is connected and you're on the Hedera mainnet. NFT loading may take a few moments. If problems persist, try refreshing your browser cache, refreshing the page, or reconnecting your wallet."
        }
      ]
    },
    {
      category: "Gallery Management",
      items: [
        {
          question: "How do I create a gallery?",
          answer: "Connect your wallet, then click 'Create New Gallery' from the dashboard. Choose a template, give your gallery a name, and start adding your NFTs to display spots."
        },
        {
          question: "How do I add NFTs to my gallery?",
          answer: "Press 'V' to enter edit mode, then look for '+' signs on walls and surfaces. Click these spots to browse and add your NFTs. You can position, rotate, and scale each piece."
        },
        {
          question: "Can I share my gallery with others?",
          answer: "Yes! Your galleries are public by default. Use the share button to get a link that anyone can view. Viewers don't need a wallet to see your gallery."
        },
        {
          question: "How many galleries can I create?",
          answer: "During beta, you can create unlimited free galleries. Premium templates will be available in future releases."
        }
      ]
    },
    {
      category: "Technical Issues",
      items: [
        {
          question: "What browsers are supported?",
          answer: "We recommend Chrome, Firefox, Safari, or Edge with WebGL support enabled. Mobile browsers may have limited functionality."
        },
        {
          question: "Why is the gallery loading slowly?",
          answer: "3D galleries require WebGL and good graphics performance. Try closing other tabs, updating your browser, or using a device with better graphics capabilities."
        },
        {
          question: "The gallery looks broken or won't load",
          answer: "Make sure WebGL is enabled in your browser settings. Try refreshing the page, clearing your browser cache, or switching to a supported browser."
        },
        {
          question: "Controls aren't working properly",
          answer: "On desktop, use mouse to look around and WASD/arrow keys to move. On mobile, use touch gestures. If controls feel stuck, click on the gallery area to regain focus."
        }
      ]
    },
    {
      category: "Account & Data",
      items: [
        {
          question: "Is my data safe?",
          answer: "Your wallet remains under your control. We only store gallery configurations and display settings. Your NFT data comes directly from the Hedera network."
        },
        {
          question: "Can I delete my galleries?",
          answer: "Yes, you can delete galleries from your dashboard. This action is permanent and cannot be undone."
        },
        {
          question: "What happens if I disconnect my wallet?",
          answer: "You'll lose access to your galleries until you reconnect. Shared gallery links will still work for viewers, but you won't be able to edit them."
        }
      ]
    },
    {
      category: "Beta & Feedback",
      items: [
        {
          question: "This is beta software - what does that mean?",
          answer: "Beta means we're still testing and improving. You may encounter bugs or incomplete features. Your feedback helps us improve! Some features may change or be added during beta."
        },
        {
          question: "How do I report bugs or suggest features?",
          answer: "Join our Discord community https://discord.gg/wWrwKhJu54 with details about any issues or suggestions. Include your browser, wallet type, and steps to reproduce problems."
        },
        {
          question: "Will my galleries be deleted when beta ends?",
          answer: "We plan to preserve beta galleries, but recommend saving screenshots of important displays as a backup during this testing phase."
        }
      ]
    }
  ];

  return (
    <div className="help-overlay">
      <div className="help-modal">
        <div className="help-header">
          <h1>Help & FAQ</h1>
          <button className="help-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        
        <div className="help-content">
          <div className="help-intro">
            <p>Find answers to common questions about using Immuta Hash Gallery. If you can't find what you're looking for, join our Discord community for support: <strong><a href="https://discord.gg/wWrwKhJu54" target="_blank" rel="noopener noreferrer">https://discord.gg/wWrwKhJu54</a></strong></p>
          </div>

          {faqItems.map((category, categoryIndex) => (
            <div key={categoryIndex} className="faq-category">
              <h2 className="faq-category-title">{category.category}</h2>
              
              {category.items.map((item, itemIndex) => {
                const globalIndex = `${categoryIndex}-${itemIndex}`;
                const isOpen = openItem === globalIndex;
                
                return (
                  <div key={itemIndex} className="faq-item">
                    <button 
                      className={`faq-question ${isOpen ? 'open' : ''}`}
                      onClick={() => toggleItem(globalIndex)}
                    >
                      <span>{item.question}</span>
                      <span className="faq-icon">{isOpen ? '−' : '+'}</span>
                    </button>
                    
                    {isOpen && (
                      <div className="faq-answer">
                        <p>{item.answer}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          <div className="help-footer">
            <div className="help-contact">
              <h3>Still need help?</h3>
              <p>Join our Discord community: <strong><a href="https://discord.gg/wWrwKhJu54" target="_blank" rel="noopener noreferrer">https://discord.gg/wWrwKhJu54</a></strong> for personalized assistance.</p>
              <p>Include details about your browser, wallet, and the specific issue you're experiencing.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;